// Order statuses as buyers and sellers think about them, shared by the dashboard Overview, the
// order lists and the order page. Each group lists the purchase-process states it covers
// (including the wallet "without-payout" and PayPal variants), so a list or a count can ask
// Sharetribe for exactly those orders.

const DAY_MS = 24 * 60 * 60 * 1000;
// Sellers are asked to ship within 7 days (the first reminder goes out then); orders still
// unshipped after 14 days are canceled automatically (server/unshipped-orders-check.js)
export const SHIP_BY_DAYS = 7;
export const AUTO_CANCEL_DAYS = 14;

const withVariants = states =>
  states.flatMap(state => [
    `state/${state}`,
    `state/${state}-without-payout`,
    `state/${state}-with-paypal`,
  ]);

export const ORDER_STATUS_GROUPS = {
  // Paid, waiting for the seller to ship
  toShip: withVariants(['purchased']),
  // The seller marked it shipped; waiting for the buyer to confirm it arrived (or the buyer
  // reported a problem with it)
  shipped: withVariants(['delivered', 'disputed']),
  // The buyer confirmed it arrived
  delivered: ['state/received', 'state/received-without-payout'],
  completed: ['state/completed', 'state/reviewed', 'state/reviewed-by-customer'],
  canceled: ['state/canceled'],
  // Offers waiting for the seller to answer
  offersWaiting: ['state/offer-pending', 'state/offer-auto-eligible-pending'],
  // The seller countered; waiting for the buyer to answer
  offersCountered: ['state/offer-countered'],
  // The seller accepted; waiting for the buyer to pay
  offersAccepted: ['state/offer-accepted'],
};

// Where a seller's money is. Stripe pays out when the buyer confirms it arrived (state received);
// wallet orders pass through received-without-payout while the wallet is credited.
export const PAYOUT_GROUPS = {
  pending: [
    ...ORDER_STATUS_GROUPS.toShip,
    ...ORDER_STATUS_GROUPS.shipped,
    'state/received-without-payout',
  ],
  paid: ['state/received', ...ORDER_STATUS_GROUPS.completed],
};

const PAYMENT_CONFIRMED = /^transition\/(custom-)?confirm-(payment|booking-without-payout)/;

/** Whether the buyer completed payment (an order abandoned or expired at checkout wasn't). */
export const isPaidOrder = transaction =>
  (transaction?.attributes?.transitions || []).some(t =>
    PAYMENT_CONFIRMED.test(t?.transition || '')
  );

/** When the order was paid: the payment confirmation, or the order's creation as a fallback. */
export const getPaidAt = transaction => {
  const transitions = transaction?.attributes?.transitions || [];
  const confirmed = transitions.find(t => PAYMENT_CONFIRMED.test(t?.transition || ''));
  const at = confirmed?.createdAt || transaction?.attributes?.createdAt;
  return at ? new Date(at) : null;
};

/**
 * For an order waiting to ship: { shipBy, cancelAt, isPastShipBy }. Ship by is 7 days after
 * payment; after that the order is canceled automatically at 14 days.
 */
export const getShipByInfo = (transaction, now = new Date()) => {
  const paidAt = getPaidAt(transaction);
  if (!paidAt) {
    return null;
  }
  const shipBy = new Date(paidAt.getTime() + SHIP_BY_DAYS * DAY_MS);
  const cancelAt = new Date(paidAt.getTime() + AUTO_CANCEL_DAYS * DAY_MS);
  return { shipBy, cancelAt, isPastShipBy: now.getTime() > shipBy.getTime() };
};

// A fixed timezone, so the server render and the browser show the same date
const shortDate = date =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  }).format(date);

/** "Ship by Nov 6", or once that's passed, "Ship now · cancels Nov 13". */
export const getShipByLabel = (transaction, now = new Date()) => {
  const info = getShipByInfo(transaction, now);
  if (!info) {
    return 'Ship now';
  }
  return info.isPastShipBy
    ? `Ship now · cancels ${shortDate(info.cancelAt)}`
    : `Ship by ${shortDate(info.shipBy)}`;
};
